<template>
  <!--
    Wraps AddResponsibilityDialog (ER-12071): name rules, Context + IQ Suggest/Stop/Re-suggest
    and Replace confirmation live there. This dialog adds the attach target row, title/CTA copy,
    and the single atomic create-draft-and-attach call (ER-12095, Responsibility Rule 5.1).
  -->
  <AddResponsibilityDialog
    v-if="featureEnabled"
    :value="value"
    :initial-value="initialName"
    :max_char="maxNameCharacters"
    :existing-names="existingNames"
    :error-message="serverError"
    :loading="isSaving"
    @input="onDialogInput"
    @onSave="handleSave"
    @clearError="serverError = ''"
  >
    <template #title>+ Add and attach Responsibility</template>

    <template #before-name>
      <AddAndAttachTargetRow
        :target-type-label="targetTypeLabel"
        :code="target.code"
        :name="target.name"
      />
    </template>

    <template #save-label>Save as Draft and Attach</template>
  </AddResponsibilityDialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import AddResponsibilityDialog from "@components/RolesCatalog/AddResponsibilityDialog/AddResponsibilityDialog.vue";
import AddAndAttachTargetRow from "@components/AddAndAttachTargetRow/AddAndAttachTargetRow.vue";
import { LD_FLAG_IDEA_MDP_617 } from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";

const featureEnabled = ref(false);

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  target: {
    type: Object,
    required: true,
    validator: (val) =>
      val &&
      (val.type === "role" || val.type === "position") &&
      val.id != null &&
      val.code != null &&
      val.name,
  },
  initialName: {
    type: String,
    default: "",
  },
  maxNameCharacters: {
    type: Number,
    default: 120,
  },
  existingNames: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["input", "created-and-attached"]);

const serverError = ref("");
const isSaving = ref(false);

const targetTypeLabel = computed(() =>
  props.target.type === "role" ? "Role" : "Position"
);

function resolveAccountId() {
  try {
    const raw = localStorage.getItem("currentAccountID");
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return parsed != null ? parsed : raw;
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function onDialogInput(nextValue) {
  if (nextValue === true) {
    emit("input", true);
    return;
  }

  serverError.value = "";
  emit("input", false);
}

async function handleSave({ name, context }) {
  const accountId = resolveAccountId();
  if (!accountId) {
    serverError.value = "Select an account to create a responsibility.";
    return;
  }

  serverError.value = "";
  isSaving.value = true;

  try {
    const payload = {
      name,
      target_type: props.target.type,
      target_id: props.target.id,
    };

    if (context) {
      payload.context = context;
    }

    const { createDraftAndAttach } = await import("@/api/responsibilityAPI");
    const response = await createDraftAndAttach(accountId, payload);

    emit("created-and-attached", response.data);
    emit("input", false);
  } catch (error) {
    if (error.response && error.response.data) {
      const data = error.response.data;
      if (data.name) {
        serverError.value = Array.isArray(data.name) ? data.name[0] : data.name;
      } else if (data.detail) {
        serverError.value = data.detail;
      } else {
        serverError.value = "Failed to create and attach responsibility. Please try again.";
      }
    } else {
      serverError.value = "Failed to create and attach responsibility. Please try again.";
    }
  } finally {
    isSaving.value = false;
  }
}

watch(
  () => props.value,
  (open) => {
    if (open) serverError.value = "";
    if (open && !featureEnabled.value) {
      emit("input", false);
    }
  }
);

watch(featureEnabled, (enabled) => {
  if (!enabled && props.value) {
    emit("input", false);
  }
});

onMounted(async () => {
  try {
    featureEnabled.value = await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_617, false);
  } catch {
    featureEnabled.value = false;
  }
  if (!featureEnabled.value && props.value) {
    emit("input", false);
  }
});
</script>
